import { ENEMIES } from "../config";

export type EnemyKey = keyof typeof ENEMIES;

export interface SpawnEntry {
  type: EnemyKey;
  count: number;
  interval: number;
}

export interface Wave {
  entries: SpawnEntry[];
  reward: number;
  boss?: boolean;
}

export const SCRIPTED_WAVE_TARGET = 60;

export type WaveTheme = "plains" | "river" | "swamp" | "snow" | "mine" | "desert" | "siege";

interface ThemeDef {
  skirmisher: EnemyKey;
  elite: EnemyKey;
  boss: EnemyKey;
}

const THEMES: Record<WaveTheme, ThemeDef> = {
  plains: { skirmisher: "kobold_rider", elite: "shieldman", boss: "boss_warlord" },
  river: { skirmisher: "cart", elite: "gargoyle", boss: "boss_sea_leviathan" },
  swamp: { skirmisher: "bog_lurker", elite: "revenant", boss: "boss_bog_giant" },
  snow: { skirmisher: "ice_elemental", elite: "wraith", boss: "boss_frost_wyrm" },
  mine: { skirmisher: "armored_orc", elite: "siege_ram", boss: "boss_rock_wyrm" },
  desert: { skirmisher: "desert_nomad", elite: "flame_imp", boss: "boss_rock_wyrm" },
  siege: { skirmisher: "shieldman", elite: "siege_ram", boss: "boss_siege_golem" },
};

export function getThemeDef(theme: WaveTheme): Readonly<ThemeDef> {
  return THEMES[theme] ?? THEMES.plains;
}

const BOSS_ROTATION: EnemyKey[] = [
  "boss_warlord",
  "boss_bridge_troll",
  "boss_frost_wyrm",
  "boss_rock_wyrm",
  "boss_bog_giant",
  "boss_sea_leviathan",
  "boss_siege_golem",
  "boss_shadow_titan",
  "boss_abyssal_dread",
  "boss_doom_bringer",
];

function scaleCount(base: number, n: number): number {
  const midGame = Math.max(0, n - 20);
  const hardGame = Math.max(0, n - 40);
  const endless = Math.max(0, n - SCRIPTED_WAVE_TARGET);
  const mult = 1 + midGame * 0.012 + hardGame * 0.018 + endless * 0.018;
  return Math.max(1, Math.round(base * mult));
}

function scaleInterval(base: number, n: number): number {
  const midGame = Math.max(0, n - 20);
  const hardGame = Math.max(0, n - 40);
  const endless = Math.max(0, n - SCRIPTED_WAVE_TARGET);
  const mult = Math.max(0.32, 1 - midGame * 0.008 - hardGame * 0.008 - endless * 0.003);
  return Math.max(90, Math.round(base * mult));
}

/**
 * Shared late-game wave generator.
 *
 * Waves 21-39 ramp steadily, 40-60 add noticeably more elite pressure, and
 * 61+ continue indefinitely with the same curve so Endless mode can keep
 * producing valid waves without storing a huge table.
 */
export function generateEscalatingWave(n: number, theme: WaveTheme = "plains"): Wave {
  const themed = getThemeDef(theme);
  const tier = Math.max(0, Math.floor((n - 1) / 5));
  const isBoss = n % 10 === 0;
  const isElite = n >= 40 || n % 5 === 0;
  const reward = Math.round(220 + n * 38 + Math.max(0, n - 40) * 25);

  if (isBoss) {
    const bossKey = n <= SCRIPTED_WAVE_TARGET
      ? BOSS_ROTATION[Math.floor(n / 10) % BOSS_ROTATION.length]
      : themed.boss;
    const bossCount = 1 + Math.floor(Math.max(0, n - SCRIPTED_WAVE_TARGET) / 20);
    return {
      reward: reward + 350,
      boss: true,
      entries: [
        { type: bossKey, count: bossCount, interval: 1200 },
        { type: "troll", count: scaleCount(3 + tier, n), interval: scaleInterval(1250, n) },
        { type: "armored_orc", count: scaleCount(8 + tier, n), interval: scaleInterval(560, n) },
        { type: "wraith", count: scaleCount(10 + tier, n), interval: scaleInterval(360, n) },
        ...(n >= 40 ? [{ type: "siege_ram" as const, count: scaleCount(1 + Math.floor(tier / 3), n), interval: scaleInterval(1800, n) }] : []),
      ],
    };
  }

  return {
    reward: reward + (isElite ? 120 : 0),
    boss: isElite && n % 5 === 0,
    entries: [
      { type: "goblin", count: scaleCount(8 + Math.floor(n * 0.25), n), interval: scaleInterval(340, n) },
      { type: "swarmling", count: scaleCount(6 + tier * 2, n), interval: scaleInterval(170, n) },
      { type: "skeleton", count: scaleCount(5 + tier, n), interval: scaleInterval(360, n) },
      { type: "orc", count: scaleCount(3 + Math.floor(tier * 0.6), n), interval: scaleInterval(660, n) },
      ...(n >= 18 ? [{ type: "flame_imp" as const, count: scaleCount(3 + tier, n), interval: scaleInterval(420, n) }] : []),
      ...(n >= 20 ? [{ type: "hellhound" as const, count: scaleCount(2 + Math.floor(tier * 0.5), n), interval: scaleInterval(400, n) }] : []),
      ...(n >= 24 ? [{ type: "wraith" as const, count: scaleCount(3 + tier, n), interval: scaleInterval(390, n) }] : []),
      ...(n >= 26 ? [{ type: "plague_carrier" as const, count: scaleCount(2 + Math.floor(tier * 0.4), n), interval: scaleInterval(750, n) }] : []),
      ...(n >= 30 ? [{ type: "armored_orc" as const, count: scaleCount(2 + Math.floor(tier * 0.8), n), interval: scaleInterval(620, n) }] : []),
      ...(n >= 32 ? [{ type: "void_specter" as const, count: scaleCount(2 + Math.floor(tier * 0.4), n), interval: scaleInterval(580, n) }] : []),
      ...(n >= 36 ? [{ type: "shieldman" as const, count: scaleCount(2 + Math.floor(tier / 2), n), interval: scaleInterval(720, n) }] : []),
      ...(n >= 38 ? [{ type: "elusive_stalker" as const, count: scaleCount(1 + Math.floor(tier / 3), n), interval: scaleInterval(500, n) }] : []),
      ...(n >= 40 ? [{ type: "gargoyle" as const, count: scaleCount(2 + Math.floor(tier / 2), n), interval: scaleInterval(640, n) }] : []),
      ...(n >= 44 ? [{ type: "golem_guardian" as const, count: scaleCount(1 + Math.floor(tier / 4), n), interval: scaleInterval(1200, n) }] : []),
      ...(n >= 45 ? [{ type: "revenant" as const, count: scaleCount(1 + Math.floor(tier / 2), n), interval: scaleInterval(820, n) }] : []),
      ...(n >= 50 ? [{ type: "ice_elemental" as const, count: scaleCount(1 + Math.floor(tier / 3), n), interval: scaleInterval(980, n) }] : []),
      ...(n >= 55 ? [{ type: "siege_ram" as const, count: scaleCount(1 + Math.floor(tier / 5), n), interval: scaleInterval(1800, n) }] : []),
      ...(n >= 60 ? [{ type: "shadow_assassin" as const, count: scaleCount(3 + Math.floor(tier / 2), n), interval: scaleInterval(560, n) }] : []),
      ...(n >= 16 ? [{ type: themed.skirmisher, count: scaleCount(2 + tier, n), interval: scaleInterval(520, n) }] : []),
      ...(n >= 40 ? [{ type: themed.elite, count: scaleCount(1 + Math.floor(tier / 2), n), interval: scaleInterval(780, n) }] : []),
    ],
  };
}

export function generateEscalatingWaves(startNumber: number, count: number, theme: WaveTheme = "plains"): Wave[] {
  return Array.from({ length: count }, (_, i) => generateEscalatingWave(startNumber + i, theme));
}
